/**
 * /connect-mac — signing Nucleus in on a Mac.
 *
 * Nucleus on the Mac holds the only sign-in on the machine and mints every
 * other app a token of its own (decision_designs_ledger/
 * 2026-09-29_the-mac-hub-holds-the-sign-in.md). It signs in here, in the
 * browser, rather than in a password field of its own: one login form to keep
 * right, not two.
 *
 * The hub listens on 127.0.0.1:<port> and opens this page with a PKCE
 * challenge. The person is asked — a click, never a silent redirect, because
 * this hands a whole sign-in to a program on their machine — and the code goes
 * to the loopback address and nowhere else. The code is bound to the
 * challenge, so it is worth nothing to anything but the process that asked.
 * Drawn by DeviceSignIn, as every program of ours signing in is; the hub holds
 * the browser until the code is exchanged and sends it back with `?done=`.
 */
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import DeviceSignIn from '../../components/grant/DeviceSignIn';

// The only place a hub code may be sent: this machine, on the port the hub
// named. Anything else in the URL is ignored rather than trusted.
export function loopbackCallback(port, code, state) {
  if (typeof port !== 'string' || !/^\d{4,5}$/.test(port)) return null;
  const p = Number(port);
  if (p < 1024 || p > 65535) return null;
  const q = new URLSearchParams({ code, state: state || '' });
  return `http://127.0.0.1:${p}/callback?${q}`;
}

export default function ConnectMacRoute() {
  const [params] = useSearchParams();
  const port = params.get('port');
  const challenge = params.get('code_challenge');

  return (
    <DeviceSignIn
      app="Nucleus" where="this Mac" testId="connect-mac"
      device={(params.get('device') || '').trim()}
      asked="pressed Sign in in Nucleus on your Mac"
      retry="Press Sign in in Nucleus on your Mac"
      can={['Do what you can do in Nucleus on the web', 'Sign your apps in on this Mac']}
      cannot={['Give an app more than you have allowed it']}
      valid={!!(loopbackCallback(port, 'x', '') && challenge && challenge.length >= 43)}
      ended={params.get('done')}
      approve={async () => {
        const r = await api.createLoginHandoffCode('nucleus-mac', challenge);
        const to = loopbackCallback(port, r.code, params.get('state'));
        if (!to) throw new Error('bad port');
        window.location.href = to;
      }}
      deny={() => {
        const to = loopbackCallback(port, '', params.get('state'));
        if (to) window.location.href = to.replace('code=&', 'error=access_denied&');
      }}
    />
  );
}
